import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import { UserContext } from '../../UserContext';
import dataService from '../../services/dataService';
import '../../features/brand/cwm-tokens.css';

/**
 * /unified/add-review — branded review flow. Rebuilds the legacy MUI
 * AddReviewScreen with:
 *   - Restaurant picker (pre-filled when arriving with location.state)
 *   - Overall star rating
 *   - Dish picker (pulls recent menu items for selected restaurant)
 *   - Safety rating (how the restaurant handled allergies)
 *   - Free-text review
 *   - Post via dataService.submitReview (falls back to optimistic success when
 *     backend rejects)
 */

const EASE = [0.22, 1, 0.36, 1];

const SAFETY_RATINGS = [
  { id: 'excellent', label: 'Excellent', sub: 'Chef confirmed, zero worry',       tone: 'green'  },
  { id: 'good',      label: 'Good',      sub: 'Server knew, no issues',            tone: 'blue'   },
  { id: 'ok',        label: 'OK',        sub: 'Handled but took effort',           tone: 'amber'  },
  { id: 'poor',      label: 'Poor',      sub: 'Uncomfortable, risky',              tone: 'red'    },
  { id: 'na',        label: 'N/A',       sub: "Didn't ask about allergies",        tone: 'gray'   },
];

const TONE = {
  green: { bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', solid: 'var(--cwm-green)' },
  blue:  { bg: 'var(--cwm-blue-pale)',   fg: 'var(--cwm-blue-deep)',  solid: 'var(--cwm-blue-deep)' },
  amber: { bg: 'var(--cwm-amber-light)', fg: 'var(--cwm-amber-text)', solid: 'var(--cwm-amber)' },
  red:   { bg: 'var(--cwm-red-light)',   fg: 'var(--cwm-red-text)',   solid: '#B91C1C' },
  gray:  { bg: 'var(--cwm-gray-100)',    fg: 'var(--cwm-gray-700)',   solid: 'var(--cwm-gray-500)' },
};

export default function UnifiedAddReview() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, isInitialized } = useContext(UserContext);
  const preselectId = location.state?.restaurantId;
  const preselectName = location.state?.restaurantName;

  const [restaurants, setRestaurants] = useState([]);
  const [restaurantId, setRestaurantId] = useState(preselectId ? String(preselectId) : '');
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [dishName, setDishName] = useState('');
  const [safetyRating, setSafetyRating] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [menuItems, setMenuItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (!isInitialized) return;
    if (!userId) { navigate('/unified'); return; }
    let cancelled = false;
    (async () => {
      try {
        const list = await dataService.listRestaurants();
        if (!cancelled) setRestaurants(Array.isArray(list) ? list : (list?.restaurants || []));
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [userId, isInitialized, navigate]);

  // Load menu items for picker when restaurant is selected
  useEffect(() => {
    if (!restaurantId) { setMenuItems([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const data = await dataService.getRestaurant(restaurantId);
        if (cancelled) return;
        const items = [];
        (data?.menus || []).forEach((m) => {
          (m.menu_data?.menus || []).forEach((menu) => {
            (menu.categories || []).forEach((cat) => {
              (cat.items || []).forEach((it) => items.push({ name: it.name, category: cat.name }));
            });
          });
        });
        setMenuItems(items);
      } catch {
        if (!cancelled) setMenuItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [restaurantId]);


  const canSubmit = restaurantId && rating > 0 && reviewText.trim().length >= 10;

  const flash = (tone, text) => {
    setAlert({ tone, text, id: Date.now() });
    setTimeout(() => setAlert(null), 3000);
  };

  const submit = async () => {
    if (!canSubmit) {
      flash('red', 'Pick a restaurant, set a star rating, and write at least one sentence.');
      return;
    }
    setSaving(true);
    try {
      await dataService.submitReview(
        userId,
        Number(restaurantId),
        -1, // chatId — not in this flow
        dishName || 'Overall experience',
        rating,
        JSON.stringify({
          safety: safetyRating,
          text: reviewText,
        }),
      );
      flash('green', 'Review posted. Thanks for sharing.');
      setTimeout(() => navigate(-1), 1200);
    } catch (e) {
      flash('red', e?.message || 'Failed to post review. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell backdrop>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '28px 20px 80px', position: 'relative', zIndex: 1 }}>

        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, ease: EASE }}
          style={{ textAlign: 'center', marginBottom: 24 }}
        >
          <div className="cwm-eyebrow-badge" style={{ marginBottom: 12 }}>
            <PenIcon size={12} /> WRITE A REVIEW
          </div>
          <h1 style={{ fontSize: 32, fontWeight: 900, letterSpacing: '-0.025em', color: 'var(--cwm-ink)', margin: '0 0 8px' }}>
            How was <em style={{ fontStyle: 'italic', color: 'var(--cwm-blue-deep)' }}>{preselectName || 'the meal'}</em>?
          </h1>
          <p style={{ maxWidth: 480, margin: '0 auto', fontSize: 14, fontWeight: 500, color: 'var(--cwm-gray-700)' }}>
            Help diners like you dine safely and well. Your review is linked to your profile.
          </p>
        </motion.section>

        {alert && (
          <div style={{
            padding: '10px 14px', borderRadius: 14, marginBottom: 16,
            background: alert.tone === 'red' ? 'var(--cwm-red-light)' : 'var(--cwm-green-light)',
            color: alert.tone === 'red' ? 'var(--cwm-red-text)' : 'var(--cwm-green-text)',
            fontSize: 13, fontWeight: 800,
          }}>{alert.text}</div>
        )}

        {/* Form */}
        <div style={{ display: 'grid', gap: 14 }}>

          {!preselectId && (
            <Card eyebrow="RESTAURANT" title="Where did you eat?">
              <SelectPill
                value={restaurantId}
                onChange={setRestaurantId}
                options={[{ value: '', label: 'Select a restaurant…' }, ...restaurants.slice(0, 100).map((r) => ({ value: String(r.id), label: r.name }))]}
              />
            </Card>
          )}

          <Card eyebrow="YOUR RATING" title={rating > 0 ? `${rating}-star experience` : "How many stars?"}>
            <div style={{ display: 'inline-flex', gap: 6 }}>
              {[1, 2, 3, 4, 5].map((n) => {
                const filled = (hover || rating) >= n;
                return (
                  <button key={n} type="button"
                          onMouseEnter={() => setHover(n)}
                          onMouseLeave={() => setHover(0)}
                          onClick={() => setRating(n)}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 2 }}>
                    <svg width="34" height="34" viewBox="0 0 24 24"
                         fill={filled ? '#F5A623' : 'none'}
                         stroke={filled ? '#F5A623' : '#DADCE0'}
                         strokeWidth="2" strokeLinejoin="round">
                      <path d="M12 2l3 7 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1z"/>
                    </svg>
                  </button>
                );
              })}
            </div>
          </Card>

          <Card eyebrow="DISH" title="What did you order?">
            <BrandInput
              value={dishName}
              onChange={setDishName}
              placeholder="e.g. Gluten-free pappardelle"
              list="cwm-review-dishes"
            />
            {menuItems.length > 0 && (
              <datalist id="cwm-review-dishes">
                {menuItems.slice(0, 50).map((it, i) => <option key={i} value={it.name}>{it.category}</option>)}
              </datalist>
            )}
            <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 6 }}>
              Optional — leave blank to review the visit overall.
            </div>
          </Card>

          <Card eyebrow="SAFETY" title="How did they handle allergies?">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
              {SAFETY_RATINGS.map((s) => {
                const t = TONE[s.tone];
                const on = safetyRating === s.id;
                return (
                  <motion.button key={s.id} type="button" onClick={() => setSafetyRating(s.id)}
                    whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
                    style={{
                      padding: '12px 14px', borderRadius: 14,
                      background: on ? t.solid : t.bg,
                      color: on ? '#fff' : t.fg,
                      border: `1.5px solid ${on ? t.solid : 'transparent'}`,
                      fontFamily: 'var(--cwm-font)', cursor: 'pointer', textAlign: 'left',
                      transition: 'all 140ms ease',
                      boxShadow: on ? `0 4px 10px ${t.solid}44` : 'none',
                    }}>
                    <div style={{ fontSize: 13, fontWeight: 900 }}>{s.label}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.88, marginTop: 2 }}>{s.sub}</div>
                  </motion.button>
                );
              })}
            </div>
          </Card>

          <Card eyebrow="YOUR EXPERIENCE" title="Tell the table what happened">
            <BrandTextArea
              value={reviewText}
              onChange={setReviewText}
              rows={5}
              placeholder="The server immediately asked about allergies, checked with the kitchen, and recommended a dish they were confident was safe…"
            />
            <div style={{ fontSize: 11.5, fontWeight: 700, color: reviewText.length < 10 ? 'var(--cwm-gray-500)' : 'var(--cwm-green-text)', marginTop: 6 }}>
              {reviewText.length < 10 ? `${10 - reviewText.length} more character${10 - reviewText.length === 1 ? '' : 's'} to go` : `${reviewText.length} characters`}
            </div>
          </Card>

        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, paddingTop: 14, borderTop: '1px solid var(--cwm-gray-100)' }}>
          <button className="cwm-btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
          <button className="cwm-btn" onClick={submit} disabled={!canSubmit || saving}
                  style={{ opacity: (!canSubmit || saving) ? 0.6 : 1, cursor: (!canSubmit || saving) ? 'not-allowed' : 'pointer' }}>
            {saving ? 'Posting…' : <><PostIcon size={13} /> Post review</>}
          </button>
        </div>
      </div>
    </AppShell>
  );
}

function Card({ eyebrow, title, children }) {
  return (
    <div className="cwm-sheet" style={{ padding: 20 }}>
      <div className="cwm-eyebrow">{eyebrow}</div>
      <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2, marginBottom: 14 }}>{title}</div>
      {children}
    </div>
  );
}

function BrandInput({ value, onChange, placeholder, list }) {
  return <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} list={list}
    style={{
      width: '100%', boxSizing: 'border-box',
      padding: '12px 16px', borderRadius: 14,
      border: '1.5px solid var(--cwm-blue-light)',
      background: '#F7FBFF', color: 'var(--cwm-ink)',
      fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 600,
      outline: 'none',
    }} />;
}

function BrandTextArea({ value, onChange, placeholder, rows = 3 }) {
  return <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows}
    style={{
      width: '100%', boxSizing: 'border-box',
      padding: '12px 16px', borderRadius: 14,
      border: '1.5px solid var(--cwm-blue-light)',
      background: '#F7FBFF', color: 'var(--cwm-ink)',
      fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 500, lineHeight: 1.5,
      outline: 'none', resize: 'vertical',
    }} />;
}

function SelectPill({ value, onChange, options }) {
  return (
    <label style={{ position: 'relative', display: 'block' }}>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%', boxSizing: 'border-box', appearance: 'none',
          padding: '12px 40px 12px 16px', borderRadius: 14,
          border: '1.5px solid var(--cwm-blue-light)',
          background: '#F7FBFF', color: 'var(--cwm-ink)',
          fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 600,
          outline: 'none', cursor: 'pointer',
        }}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
           style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: 'var(--cwm-blue-deep)', pointerEvents: 'none' }}>
        <path d="M6 9l6 6 6-6"/>
      </svg>
    </label>
  );
}

function PenIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4v16h16v-7M18 2l4 4-10 10h-4v-4z"/></svg>; }
function PostIcon({ size = 13 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4z"/></svg>; }

